import { useEffect, useState } from 'react'
import { auth, db } from '../firebase'
import { off, ref, query, orderByChild, equalTo, onValue } from 'firebase/database'

const getRefPath = ({ view, uid }) => {
  switch (view) {
    case 'owned': return 'posts'
    case 'voted': return `userActivity/${uid}/votedPosts`
    case 'viewed': return `userActivity/${uid}/viewedPosts`
    default: return 'posts'
  }
}

const getQuery = ({ view, ref, uid }) => {
  switch (view) {
    case 'owned': return query(ref, orderByChild('ownerId'), equalTo(uid))
    case 'voted': return query(ref, orderByChild('timestamp'))
    case 'viewed': return query(ref, orderByChild('timestamp'))
    default: return query(ref, orderByChild('ownerId'), equalTo(uid))
  }
}

const useProfilePosts = ({ view, limit: initialLimit = 10 }) => {
  const uid = auth.currentUser.uid

  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState([])
  const [limit, setLimit] = useState(initialLimit)
  const [hasMore, setHasMore] = useState(true)
  const [, setPreviousCount] = useState(0)

  let postsRef

  const showMore = () => {
    setLimit(limit + 10)
  }

  useEffect(() => {
    const update = async () => {
      if (postsRef) {
        off(postsRef)
      }

      postsRef = ref(db, getRefPath({ view, uid }))
      onValue(getQuery({ view, ref: postsRef, uid }), (response) => {
        const data = response.val()
        const allPosts = []
        if (data) {
          for (const [key, value] of Object.entries(data)) {
            // For voted/viewed tabs, the data structure is different
            // It contains references to posts, not the posts themselves
            if (view === 'voted' || view === 'viewed') {
              // Skip if the post data doesn't have essential fields (post was deleted)
              if (value && value.postTitle) {
                allPosts.push({
                  ...value,
                  id: key,
                  title: value.postTitle // Use postTitle for these views
                })
              }
            } else {
              // For owned posts, we have the full post data
              allPosts.push({
                ...value,
                id: key
              })
            }
          }
        }

        // Sort posts by timestamp, newest first
        if (view === 'owned') {
          allPosts.sort((a, b) => {
            const timeA = a.createdAt || 0
            const timeB = b.createdAt || 0
            return timeB - timeA // Descending order (newest first)
          })
        } else {
          // For voted and viewed, timestamps might be negative (Firebase pattern)
          // If negative, more negative = newer, so we reverse the comparison
          allPosts.sort((a, b) => {
            const timeA = a.timestamp || 0
            const timeB = b.timestamp || 0
            // If timestamps are negative, reverse the sort
            if (timeA < 0 && timeB < 0) {
              return timeA - timeB // More negative = newer
            }
            return timeB - timeA // Normal: larger = newer
          })
        }

        // Apply client-side limit
        const convertedPosts = allPosts.slice(0, limit)

        if (allPosts.length <= limit) {
          setHasMore(false)
        } else {
          setHasMore(true)
        }

        setPreviousCount(convertedPosts.length)
        setPosts(convertedPosts)
        setLoading(false)
      })
    }

    update()

    return () => {
      if (postsRef) {
        off(postsRef)
      }
    }
  }, [limit, view, uid])

  return {
    posts,
    loading,
    showMore: hasMore ? showMore : undefined
  }
}

export default useProfilePosts
